import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { ChevronDown } from 'lucide-react';
import useTranslation from 'next-translate/useTranslation';

import { Accordion, AccordionDetails, AccordionSummary, ListItem } from '@mui/material';

import type { NotificationGroup, NotificationType } from './types';

import { BodyText } from '../../core/typography';
import GeneralNotification from './generalNotification';

const NotificationItem: React.FC<{
  notification: NotificationType;
  onViewEnter: (id: string, read: boolean) => void;
}> = ({ notification, onViewEnter }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        onViewEnter(notification.id, Boolean(notification.readAt));
      }
    },
    rootMargin: '0px 0px -50px 0px',
    threshold: 0.5,
    triggerOnce: true,
  });
  const read = Boolean(notification.readAt);
  return (
    <ListItem
      sx={{
        backgroundColor: read ? 'transparent' : 'rgba(10, 115, 224, 0.08)',
        borderBottom: 1,
        borderColor: 'divider',
        borderLeft: read ? '4px solid transparent' : '4px solid #82A3C5',
      }}
      data-notification-id={notification.id}
      data-read={read}
      key={notification.id}
      ref={ref}
    >
      <GeneralNotification notification={notification} />
    </ListItem>
  );
};

const NotificationsGroup: React.FC<{
  group: NotificationGroup;
  onViewEnter: (id: string, read: boolean) => void;
}> = ({ group, onViewEnter }) => {
  const { t } = useTranslation('notifications');
  const [expanded, setExpanded] = useState(false);
  const allUnread = group.items.every((item) => !item.readAt);
  const hasUnread = group.items.some((item) => !item.readAt);
  const summaryBackgroundColor = allUnread
    ? 'rgba(10, 115, 224, 0.08)'
    : hasUnread
      ? 'rgba(10, 115, 224, 0.04)'
      : 'transparent';
  const summaryBorderLeftColor = allUnread ? '#82A3C5' : hasUnread ? '#A3C5E6' : 'transparent';

  const handleExpansionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  return (
    <Accordion
      sx={{
        '::before': {
          backgroundColor: 'transparent',
        },
        boxShadow: 'none',
        margin: '0 !important',
      }}
      expanded={expanded}
      onChange={handleExpansionChange}
    >
      <AccordionSummary
        expandIcon={
          <ChevronDown
            style={{
              transform: expanded ? 'rotate(-180deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s ease-in-out',
            }}
            size={18}
          />
        }
        sx={{
          '.Mui-expanded': {
            borderBottom: 'none !important',
            margin: '12px 0 !important',
          },
          '.MuiAccordionSummary-root': {
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            borderTop: 'none',
          },
          backgroundColor: summaryBackgroundColor,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          borderLeft: `4px solid ${summaryBorderLeftColor}`,
          minHeight: '48px !important',
          px: 1.5,
        }}
      >
        <BodyText>{t(group.type, { count: group.items.length })}</BodyText>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          overflowY: 'auto',
          scrollbarWidth: 'none',
        }}
        data-group-id={group.id}
      >
        {group.items.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onViewEnter={onViewEnter}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};
export default NotificationsGroup;
